// dependencies
import { format, isValid, parseISO } from "date-fns";
import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { useResizeObserver } from "usehooks-ts";

import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import { Avatar } from "@performyard/py-components";

import ProgressBar from "../../../../components/ProgressBar";
import Typography from "../../../../components/Text";
import TruncatedText from "../../../../components/Text/TruncatedText";
import Tooltip from "../../../../components/Tooltip";

//hooks
import { useUserDescendants } from "../../../../api/hooks";

// types
import { Goal } from "../../../../api/services/searchServiceResultTypes/goal";

import { getUser } from "../../../../selectors/app";

// utils
import get from "../../../../utils/GetNested";

// constants
import GoalConstants from "../../../../constants/Goals";
// styles
import css from "./Style.module.scss";

type GoalCardProps = {
	isActive?: boolean;
	onClick?: (goal: any) => void;
	style?: React.CSSProperties;
	className?: string;
	goal: Goal;
};

const GoalCard = ({ isActive = false, onClick = undefined, style = {}, className = "", goal }: GoalCardProps) => {
	const cardRef = useRef<HTMLDivElement>(null);
	const { width = 0 } = useResizeObserver<HTMLDivElement>({
		ref: cardRef,
	});
	const percentRef = useRef<HTMLDivElement>(null);
	const { width: percentWidth } = useResizeObserver<HTMLDivElement>({
		ref: percentRef,
	});
	const user = useSelector((state) => getUser(state)).toJS();
	const { data: descendantsData } = useUserDescendants(user.id);
	const usersInDescendants = descendantsData?.descendants || [];

	const handleClick = () => {
		if (onClick) {
			onClick(goal);
		}
	};

	const handleLinkClick = (e) => {
		// e.preventDefault();
		e.stopPropagation();
	};

	const baseStyle = {
		...style,
	};

	const percentComplete = goal.progress * 100;
	const needTooltip = percentComplete > 999 || percentComplete < -999;
	const isTopGoal = goal.isTopLevel;
	const goalColor = get(goal, ["outlook", "color"], "#404041");

	const isValidDueDate = isValid(parseISO(goal?.endDate ? goal.endDate : ""));
	const canUserViewDetails =
		get(goal, "canUserViewGoalDetails", true) &&
		(user.isAdmin ||
			get(goal, ["assignee", "_id"]) === user.id ||
			usersInDescendants.some((id) => id === get(goal, ["assignee", "_id"])));

	return (
		<div
			ref={cardRef}
			data-testid="goalCard"
			className={`${css.Main} ${className} ${isTopGoal ? css.isTopGoal : null} ${
				isActive ? css.isActiveGoal : ""
			}`}
			style={baseStyle}
		>
			<div className={css.card} onClick={handleClick}>
				<div className={css.content}>
					<div>
						<div className={css.goalHeaderContainer}>
							<div className={css.goalNameContainer}>
								{isTopGoal && (
									<div
										data-testid="topGoalStar"
										title="Top Level Goal"
										className={css.isTopStar}
										style={{ color: goalColor }}
									>
										<FontAwesomeIcon icon={["fas", "star"]} size="sm" />
									</div>
								)}

								<TruncatedText
									maxWidth={isTopGoal ? width - 92 : width - 64}
									size="paragraph-3"
									weight="semi-bold"
									color="neutral-8"
									text={get(goal, "name", "")}
								/>
							</div>
							{canUserViewDetails ? (
								<a
									data-testid="viewGoalButton"
									target="_blank"
									rel="noopener noreferrer"
									onClick={handleLinkClick}
									href={`/company/goal/${get(goal, "_id")}/`}
									className={`no-goal-tooltip ${css.openGoalButton}`}
								>
									<div className={css.openGoalButtonIcon}>
										<FontAwesomeIcon icon={faArrowUpRightFromSquare} />
									</div>
								</a>
							) : null}
						</div>

						<div className={css.goalDetails}>
							<div data-testid="assigneeContainer" className={css.authorContainer}>
								<Avatar
									size={24}
									dangerouslySetClassName={css.authorAvatar}
									id={get(goal, ["assignee", "_id"], "")}
									alt={`${get(goal, ["assignee", "name"], "")}'s avatar`}
								/>
								<TruncatedText
									maxWidth={width - 80}
									size="paragraph-3"
									weight="semi-bold"
									text={get(goal, ["assignee", "name"], "")}
									className={css.authorName}
								/>
							</div>

							<div className={css.progressBarContainer}>
								<div className={css.progressBar}>
									<ProgressBar
										progress={percentComplete}
										showAmount={false}
										striped={false}
										animated={false}
										color={goalColor}
										width={width - (percentWidth || 0) - 36}
									/>
								</div>
								<Tooltip message={`${percentComplete}%`} disabled={!needTooltip}>
									<div ref={percentRef}>
										<Typography weight="regular" size="paragraph-3">
											{needTooltip
												? `${percentComplete < 999 ? "<-" : ">"}999`
												: Math.floor(percentComplete)}
											%
										</Typography>
									</div>
								</Tooltip>
							</div>

							<div data-testid="detailsContainer" className={css.additionalDetails}>
								<div className={`${css.detail} ${css.highlight}`}>
									<Typography size="caption" weight="semi-bold">
										Status
									</Typography>
									<Typography size="caption" weight="regular">
										{
											get(
												GoalConstants,
												["statusLabels", get(goal, "status", "")],
												get(goal, "status", "")
											) as string
										}
									</Typography>
								</div>

								<div className={`${css.detail} ${css.highlight}`}>
									<Typography size="caption" weight="semi-bold">
										Due Date
									</Typography>
									<Typography size="caption" weight="regular">
										{isValidDueDate ? format(parseISO(goal.endDate), "MMM d, yyyy") : " "}
									</Typography>
								</div>

								<div className={`${css.detail} ${css.highlight}`}>
									<Typography size="caption" weight="semi-bold">
										Aligned Goals
									</Typography>
									<Typography size="caption" weight="regular">
										{get(goal, "descendantsCount", 0)}
									</Typography>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default GoalCard;
